import type { CallbackInterface } from "recoil";

import { logAppError } from "Helpers/logger";

import type { PageStateType } from "Types/state";

import { errorUpdater, loaderUpdater, pageState } from "State/page";

import { fetchResultsCallback } from "Pages/RecipientsList/recoil/callbacks/callbacks";
import { notifyTrackingUpdateSuccess } from "Pages/RecipientsList/recoil/services/notifications";
import {
  fetchRecipientTrackingChangeLogs,
  getEmailRecipientTrackingInsertDate,
  updateRecipientTracking,
} from "Pages/RecipientsList/recoil/services/recipient";
import { modalRecipientTrackingState } from "Pages/RecipientsList/recoil/state/modalRecipientTracking";
import {
  panelEditRecipientState,
  recipientTrackingChangeLogsState,
} from "Pages/RecipientsList/recoil/state/recipient";

export const modalRecipientTrackingOpenCallback =
  ({ snapshot, set }: CallbackInterface) =>
  async (idRecipient: number, tracking: boolean) => {
    set(modalRecipientTrackingState, {
      show: true,
      idRecipient,
      tracking,
    });
  };

export const modalRecipientTrackingConfirmCallback =
  ({ snapshot, set, reset, ...cbi }: CallbackInterface) =>
  async () => {
    reset(modalRecipientTrackingState);
    set<PageStateType>(pageState, loaderUpdater(true));

    const m = await snapshot.getPromise(modalRecipientTrackingState);

    try {
      if (m.show) {
        const newTracking = !m.tracking;
        await updateRecipientTracking(m.idRecipient, newTracking);
        const trackingInsertDate = await getEmailRecipientTrackingInsertDate(
          m.idRecipient,
        );

        set(panelEditRecipientState, (prev) => ({
          ...prev,
          tracking: newTracking,
          trackingInsertDate: trackingInsertDate?.InsertDate,
          idNLFrom: trackingInsertDate?.IdNLFrom,
        }));

        notifyTrackingUpdateSuccess(newTracking);

        const prev = await snapshot.getPromise(
          recipientTrackingChangeLogsState,
        );
        const changelogResult = await fetchRecipientTrackingChangeLogs(
          m.idRecipient,
          {
            pageNumber: 1,
            pageSize: prev.pageSize,
            orderBy: "InsertDate",
            orderDescending: true,
          },
        );
        set(recipientTrackingChangeLogsState, (s) => ({
          ...s,
          ...changelogResult,
          status: "ready" as const,
        }));

        await fetchResultsCallback({ set, snapshot, reset, ...cbi })({});
      }
    } catch (error) {
      logAppError({
        Component: "RecipientsList",
        Method: "modalRecipientTrackingConfirmCallback",
        appError: error,
      });

      set<PageStateType>(pageState, errorUpdater);
    }

    set<PageStateType>(pageState, loaderUpdater(false));
  };

export const modalRecipientTrackingCancelCallback =
  ({ reset }: CallbackInterface) =>
  async () => {
    reset(modalRecipientTrackingState);
  };
